'use client'

import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { useCreateEmptyBrandMutation } from '@/api/brands/mutations/create-empty-brand-mutation'
import { useCreateEmptyProductMutation } from '@/api/products/mutations/create-empty-product-mutation'
import { useAgentStore } from '@/features/agents/stores/agent-store'
import { parseBrandUuidFromSegment } from '@/features/brands/brand-url-identifier'
import { Route, route } from '@/setup/routing/routing'
import { toast } from '@/features/toast/toast'

type OnCreated = (brandUrl: string) => void

export interface ICreateBrandFlowModalContext {
    openCreateBrandModal: (onCreated?: OnCreated, variantId?: string) => void
    openCreateProductModal: (onCreated?: OnCreated) => void
    isCreatingBrand: boolean
    isCreatingProduct: boolean
}

const CreateBrandFlowModalContext = createContext<ICreateBrandFlowModalContext>({
    openCreateBrandModal: () => {},
    openCreateProductModal: () => {},
    isCreatingBrand: false,
    isCreatingProduct: false,
})

/** crypto.randomUUID with a Math.random fallback for very old browsers. */
function makeIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function CreateBrandFlowModalProvider({ children }: { children: ReactNode }) {
    const router = useRouter()
    const params = useParams()
    const searchParams = useSearchParams()

    const { mutateAsync: createEmptyBrand, isPending: isCreatingBrand } = useCreateEmptyBrandMutation()
    const { mutateAsync: createEmptyProduct, isPending: isCreatingProduct } = useCreateEmptyProductMutation()
    const variantOverride = useAgentStore((s) => s.variantOverride)
    const startingModeOverride = useAgentStore((s) => s.startingModeOverride)

    // Synchronous guards: `mutation.isPending` only flips AFTER the mutation call
    // has run. A same-tick double-click reads `isPending===false` on both fires.
    // These refs are set inside the click handler BEFORE the mutation call,
    // so a second click in the same tick is dropped.
    const brandInFlightRef = useRef(false)
    const productInFlightRef = useRef(false)

    const openCreateBrandModal = useCallback(
        (onCreated?: OnCreated, variantId?: string) => {
            if (brandInFlightRef.current) return
            brandInFlightRef.current = true

            // Idempotency key protects against transport-level retries hitting
            // different pods. Server-side dedupe returns the same brand for
            // duplicate requests within a 60s window (see
            // BrandCreationIdempotencyService).
            const idempotencyKey = makeIdempotencyKey()

            // `startingModeOverride` is the superuser agent-settings testing
            // override; the backend ignores it for non-superusers.
            createEmptyBrand({
                variantId: variantId ?? variantOverride ?? undefined,
                currentModeId: startingModeOverride ?? undefined,
                idempotencyKey,
            } as Parameters<typeof createEmptyBrand>[0])
                .then((result) => {
                    // Route by UUID — slug is unstable while the brand is
                    // empty (agent will rename it during onboarding). Middleware
                    // accepts either UUID or slug and resolves to the same brand.
                    // BrandContext will silently swap the URL to the pretty slug
                    // once `emptyBrand` flips to false.
                    const agentTaskUrl = route(Route.AGENT_TASK).getUrl({
                        brandSlug: result.emptyBrand.uuid,
                        agent_uuid: result.agent.uuid,
                    })

                    if (onCreated) {
                        onCreated(agentTaskUrl)
                    } else {
                        // A new brand changes the dynamic brand segment, so it must enter through a
                        // fresh route tree instead of the current brand's intercepted task slot.
                        window.location.assign(agentTaskUrl)
                    }
                })
                .catch(() => {
                    toast.error("Couldn't create a new brand.", {
                        action: {
                            label: 'Try again',
                            onClick: () => {
                                brandInFlightRef.current = false
                                openCreateBrandModal(onCreated, variantId)
                            },
                        },
                        duration: 8000,
                    })
                })
                .finally(() => {
                    brandInFlightRef.current = false
                })
        },
        [
            createEmptyBrand,
            variantOverride,
            startingModeOverride,
        ]
    )

    // Product creation stays client-side — it's intra-brand (same layout tree,
    // same brand context), so the cross-brand stale-promise class doesn't apply.
    const openCreateProductModal = useCallback(
        (onCreated?: OnCreated) => {
            if (productInFlightRef.current) return

            // The URL segment is the pretty `slug-{uuid}` form — submit the
            // bare uuid identity to the API (frontend does the splitting;
            // backend parsing is only defense-in-depth for other clients).
            const rawSegment = (params?.brandSlug as string) || searchParams.get('brandSlug')
            const brandSlug = rawSegment ? (parseBrandUuidFromSegment(rawSegment) ?? rawSegment) : rawSegment
            if (!brandSlug) {
                toast.error('Open a brand first to create a product.')
                return
            }

            productInFlightRef.current = true

            createEmptyProduct({
                brandSlug,
                variantId: variantOverride ?? undefined,
                currentModeId: startingModeOverride ?? undefined,
            })
                .then((result) => {
                    // Navigate with the pretty segment (URL stays readable);
                    // the uuid identity was only for the API call above.
                    const agentTaskUrl = route(Route.AGENT_TASK).getUrl({
                        brandSlug: rawSegment ?? brandSlug,
                        agent_uuid: result.agent.uuid,
                    })
                    if (onCreated) {
                        onCreated(agentTaskUrl)
                    } else {
                        router.push(agentTaskUrl)
                    }
                })
                .catch(() => {
                    toast.error("Couldn't create a new product.", {
                        action: {
                            label: 'Try again',
                            onClick: () => {
                                productInFlightRef.current = false
                                openCreateProductModal(onCreated)
                            },
                        },
                        duration: 8000,
                    })
                })
                .finally(() => {
                    productInFlightRef.current = false
                })
        },
        [createEmptyProduct, router, params?.brandSlug, searchParams, variantOverride, startingModeOverride]
    )

    return (
        <CreateBrandFlowModalContext.Provider
            value={{ openCreateBrandModal, openCreateProductModal, isCreatingBrand, isCreatingProduct }}
        >
            {children}
        </CreateBrandFlowModalContext.Provider>
    )
}

export const useCreateBrandFlowModal = () => useContext(CreateBrandFlowModalContext)
